// src/pages/DashboardPage.tsx

import React, { useEffect, useState } from 'react';
import { useAuthContext } from '../context/AuthContext';
import { packageService } from '../services/package.service';
import { PackageStatus, PACKAGE_STATUS_LABELS, PACKAGE_STATUS_COLORS } from '../types/package.types';
import { StatusBadge } from '../components/ui/StatusBadge';

const TRACKED_STATUSES: PackageStatus[] = ['CREATED', 'IN_TRANSIT','OUT_FOR_DELIVERY','DELIVERY_FAILED', 'DELIVERED', 'RETURNED'];

interface StatCard { label: string; value: number; status: PackageStatus; }

const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrador', OPERATOR: 'Operador', VIEWER: 'Visualizador',
};

export const DashboardPage: React.FC = () => {
  const { user } = useAuthContext();
  const [stats, setStats]     = useState<StatCard[]>([]);
  const [loading, setLoading] = useState(true);

  const role = user?.roles.split(',')[0]?.trim() ?? '';

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const results = await Promise.allSettled(
          TRACKED_STATUSES.map(s => packageService.getByStatus(s))
        );
        const cards = TRACKED_STATUSES.map((s, i) => ({
          status: s,
          label:  PACKAGE_STATUS_LABELS[s],
          value:  results[i].status === 'fulfilled' ? (results[i] as PromiseFulfilledResult<any[]>).value.length : 0,
        }));
        setStats(cards);
      } catch { /* silently fail */ } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Buenos días' : hour < 18 ? 'Buenas tardes' : 'Buenas noches';

  return (
    <div className="dashboard-page">
      {/* Welcome */}
      <div className="dashboard-page__welcome">
        <div>
          <h1 className="dashboard-page__title">
            {greeting}, <em>{user?.email.split('@')[0]}</em>
          </h1>
          <p className="dashboard-page__subtitle">
            Sesión activa como <strong>{ROLE_LABELS[role] ?? role}</strong>
          </p>
        </div>
        <div className="dashboard-page__date">
          {new Date().toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* Stats grid */}
      <div className="dashboard-stats">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="stat-card stat-card--skeleton" />
          ))
        ) : (
          stats.map(card => (
            <div key={card.status} className="stat-card">
              <div
                className="stat-card__indicator"
                style={{ backgroundColor: PACKAGE_STATUS_COLORS[card.status] }}
              />
              <div className="stat-card__value">{card.value}</div>
              <div className="stat-card__label">{card.label}</div>
              <StatusBadge status={card.status} />
            </div>
          ))
        )}
      </div>

      {/* Quick info by role */}
      <div className="dashboard-info">
        <h2 className="dashboard-info__title">Acceso rápido</h2>
        <div className="dashboard-info__grid">
          <div className="info-card">
            <h3 className="info-card__title">Ver paquetes</h3>
            <p className="info-card__text">Consulta el estado y la ubicación de todos los paquetes en tiempo real.</p>
            <a href="/packages" className="lgt-btn lgt-btn--outline" style={{ display: 'inline-flex', marginTop: '1rem' }}>
              Ir a paquetes →
            </a>
          </div>
          {role !== 'VIEWER' && (
            <div className="info-card">
              <h3 className="info-card__title">Registrar paquete</h3>
              <p className="info-card__text">Crea un nuevo paquete con los datos del destinatario y dimensiones.</p>
              <a href="/packages" className="lgt-btn lgt-btn--primary" style={{ display: 'inline-flex', marginTop: '1rem' }}>
                Crear paquete →
              </a>
            </div>
          )}
          {role === 'ADMIN' && (
            <div className="info-card info-card--admin">
              <h3 className="info-card__title">Administración</h3>
              <p className="info-card__text">Como administrador puedes eliminar paquetes y gestionar todos los estados.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
